// Timing helper for the performance plan (performance-plan.md, Phase 0).
// On for localhost, or after opening any page with ?perf=1 (remembered for
// the browser tab). Prints one table per session to the console.

// main.js replaces console methods outside localhost; this module is
// evaluated before that, so keep the real ones.
const out = {
  log: console.log.bind(console),
  table: console.table.bind(console),
};

const enabled = (() => {
  if (typeof window === "undefined" || !window.performance) return false;
  try {
    if (new URLSearchParams(window.location.search).has("perf")) {
      sessionStorage.setItem("perf", "1");
    }
    return (
      window.location.hostname === "localhost" ||
      sessionStorage.getItem("perf") === "1"
    );
  } catch (e) {
    return window.location.hostname === "localhost";
  }
})();

const sessions = {};

// Starts a session. t0 = 0 measures from the start of the page load.
export function perfStart(id, t0 = performance.now()) {
  if (!enabled) return;
  const session = { t0, marks: [], longTasks: [], observer: null };
  if (typeof PerformanceObserver !== "undefined") {
    try {
      session.observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((e) => {
          if (e.startTime >= session.t0) session.longTasks.push(e.duration);
        });
      });
      session.observer.observe({ type: "longtask", buffered: t0 === 0 });
    } catch (e) {
      session.observer = null;
    }
  }
  if (sessions[id]?.observer) sessions[id].observer.disconnect();
  sessions[id] = session;
}

// Records a named point once per session; later marks with the same name are ignored.
export function perfMark(id, name) {
  const session = sessions[id];
  if (!enabled || !session || session.marks.some((m) => m.name === name)) return;
  session.marks.push({ name, at: performance.now() });
}

// Records a mark after the browser has painted the current changes.
export function perfMarkPaint(id, name) {
  if (!enabled || !sessions[id]) return;
  requestAnimationFrame(() => setTimeout(() => perfMark(id, name), 0));
}

// Marks the end once the browser has painted, then prints the report.
export function perfEnd(id, name = "painted") {
  if (!enabled || !sessions[id]) return;
  requestAnimationFrame(() =>
    setTimeout(() => {
      perfMark(id, name);
      perfReport(id);
    }, 0)
  );
}

export function perfReport(id) {
  const session = sessions[id];
  if (!enabled || !session) return;
  delete sessions[id];
  if (session.observer) session.observer.disconnect();

  let previous = session.t0;
  const rows = session.marks.map((m) => {
    const row = {
      step: m.name,
      "ms from start": Math.round(m.at - session.t0),
      "ms from previous": Math.round(m.at - previous),
    };
    previous = m.at;
    return row;
  });
  const tasks = session.longTasks;
  out.log(`[perf] ${id}`);
  out.table(rows);
  out.table({
    "long tasks > 50 ms": tasks.filter((d) => d > 50).length,
    "long tasks > 100 ms": tasks.filter((d) => d > 100).length,
    "longest task ms": Math.round(Math.max(0, ...tasks)),
    "DOM elements": document.getElementsByTagName("*").length,
    "JS heap MB": performance.memory
      ? Math.round(performance.memory.usedJSHeapSize / 1048576)
      : "n/a",
  });
}
